// pages/admin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { 
  DollarSign, TrendingUp, Users, Calendar, 
  Bus, Ticket, CheckCircle, Clock, XCircle
} from 'lucide-react';
import { adminAPI } from '../../services/api';

const Dashboard = () => {
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reservasPorDia, setReservasPorDia] = useState([]);
  const [serviciosPopulares, setServiciosPopulares] = useState([]);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      // Cargar estadísticas
      const statsData = await adminAPI.getEstadisticas();
      setEstadisticas(statsData);
      setReservasPorDia(statsData.reservasPorDia || []);

      // Cargar servicios para calcular populares
      const serviciosData = await adminAPI.listar();
      
      // Simular reservas por servicio (en producción vendría del backend)
      const serviciosConReservas = serviciosData.map((servicio) => ({
        ...servicio,
        cantidadReservas: Math.floor(Math.random() * 20) + 1,
        recaudacion: Math.floor(Math.random() * 500000) + 100000
      }));
      
      const topServicios = serviciosConReservas
        .sort((a, b) => b.cantidadReservas - a.cantidadReservas)
        .slice(0, 5);
      setServiciosPopulares(topServicios);
      
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrecio = (precio) => {
    return new Intl.NumberFormat('es-AR').format(precio);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    return new Date(fecha).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short'
    });
  };

  const getPorcentajeCrecimiento = () => {
    if (!reservasPorDia || reservasPorDia.length < 2) return 0;
    const hoy = reservasPorDia[0]?.cantidad || 0;
    const ayer = reservasPorDia[1]?.cantidad || 0;
    if (ayer === 0) return hoy > 0 ? 100 : 0;
    return Math.round(((hoy - ayer) / ayer) * 100);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
        </div>
      </AdminLayout>
    );
  }

  const crecimiento = getPorcentajeCrecimiento();

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-500 text-sm">Visión general del negocio</p>
        </div>

        {/* Tarjetas de estadísticas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total recaudado</p>
                <p className="text-2xl font-bold text-green-600">
                  ${formatPrecio(estadisticas?.totalRecaudado || 0)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total reservas</p>
                <p className="text-2xl font-bold text-gray-800">
                  {estadisticas?.totalReservas || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Ticket className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            {crecimiento !== 0 && (
              <div className={`mt-2 text-xs flex items-center gap-1 ${crecimiento > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {crecimiento > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingUp className="w-3 h-3 rotate-180" />}
                <span>{Math.abs(crecimiento)}% vs ayer</span>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Reservas pendientes</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {estadisticas?.pendientes || 0}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Reservas pagadas</p>
                <p className="text-2xl font-bold text-blue-600">
                  {estadisticas?.pagados || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Gráfico de reservas por día */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Reservas de los últimos 7 días
            </h3>
            
            {reservasPorDia.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay datos disponibles
              </div>
            ) : (
              <div className="space-y-3">
                {reservasPorDia.map((dia, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600">{formatFecha(dia.fecha)}</span>
                      <span className="font-medium text-gray-800">{dia.cantidad} reservas</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ 
                          width: `${Math.min(100, (dia.cantidad / Math.max(...reservasPorDia.map(d => d.cantidad), 1)) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Servicios más populares */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Bus className="w-4 h-4" />
              Servicios más populares
            </h3>
            
            {serviciosPopulares.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay datos disponibles
              </div>
            ) : (
              <div className="space-y-4">
                {serviciosPopulares.map((servicio, idx) => (
                  <div key={servicio.id} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800">
                          {servicio.origen} → {servicio.destino}
                        </span>
                        <span className="text-xs text-gray-400">
                          {servicio.empresa}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-gray-500">
                          {servicio.cantidadReservas} reservas
                        </span>
                        <span className="text-xs text-green-600">
                          ${formatPrecio(servicio.recaudacion)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-800">
                        #{idx + 1}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Resumen rápido */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-100">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-yellow-600" />
              <div>
                <p className="text-sm text-yellow-700">Reservas pendientes de pago</p>
                <p className="text-xl font-bold text-yellow-800">{estadisticas?.pendientes || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 rounded-xl p-4 border border-red-100">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-sm text-red-700">Reservas canceladas</p>
                <p className="text-xl font-bold text-red-800">{estadisticas?.cancelados || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-xl p-4 border border-green-100">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Tasa de conversión</p>
                <p className="text-xl font-bold text-green-800">
                  {estadisticas?.totalReservas > 0 
                    ? Math.round((estadisticas.pagados / estadisticas.totalReservas) * 100) 
                    : 0}%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;